import React from 'react';
import { SessionDetail } from '../../types/superadmin';
import { EmptyState, ScoreCard } from '../primitives';
import { fmtInt } from '../../lib/format';
import { formatDuration } from '../../lib/session';

/**
 * Talk-share and volume for one call: the headline counts, then the
 * per-participant rollup post-call analysis writes.
 *
 * `perParticipant` is empty on legacy sessions recorded before participant
 * identity existed — the table simply does not render rather than showing a
 * row of zeroes that reads as "nobody spoke".
 */

export default function SessionStats({ session }: { session: SessionDetail }) {
  const stats = session.statistics ?? {};
  const perParticipant = stats.perParticipant ?? [];
  const totalWords = perParticipant.reduce((sum, row) => sum + (row.wordCount ?? 0), 0);

  return (
    <div className="session-stats">
      <div className="scorecard">
        <ScoreCard label="Duration" value={formatDuration(session.callDuration)} />
        <ScoreCard label="Transcript lines" value={fmtInt(stats.totalTranscripts ?? 0)} />
        <ScoreCard
          label="Salesperson lines"
          value={fmtInt(stats.salespersonTranscripts ?? 0)}
          sub={`${fmtInt(stats.customerTranscripts ?? 0)} from customers`}
        />
        <ScoreCard
          label="Speech-rate alerts"
          value={fmtInt(session.speechRateAlerts?.length ?? 0)}
          sub="Moments the rep spoke too fast"
        />
      </div>

      <section className="section">
        <div className="section-head">
          <div>
            <h3>By participant</h3>
            <p className="desc">Lines and words spoken, as counted by post-call analysis.</p>
          </div>
        </div>
        {perParticipant.length === 0 ? (
          <EmptyState>
            This session predates per-participant attribution, so only the totals above are
            available.
          </EmptyState>
        ) : (
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>Participant</th>
                  <th style={{ width: 120 }}>Role</th>
                  <th style={{ width: 120 }}>Lines</th>
                  <th style={{ width: 120 }}>Words</th>
                  <th style={{ width: 140 }}>Share of words</th>
                </tr>
              </thead>
              <tbody>
                {perParticipant.map((row, index) => {
                  const words = row.wordCount ?? 0;
                  const share = totalWords > 0 ? Math.round((words / totalWords) * 100) : 0;
                  return (
                    <tr key={String(row.participantId ?? index)}>
                      <td>{row.name || String(row.participantId ?? '—')}</td>
                      <td>
                        <span className={'chip ' + (row.role === 'salesperson' ? 'info' : '')}>
                          {row.role || '—'}
                        </span>
                      </td>
                      <td>
                        <span className="metric-num">{fmtInt(row.transcriptCount ?? 0)}</span>
                      </td>
                      <td>
                        <span className="metric-num">{fmtInt(words)}</span>
                      </td>
                      <td>
                        <span className="metric-num">
                          {share}
                          <span className="u">%</span>
                        </span>
                        <span className="row-bar">
                          <span style={{ width: share + '%' }} />
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
